import React, { useContext, useEffect, useState } from "react";
import { useDistributorsContext } from "./distributorsContext";
import { INTERNAL_SKU_LOCAL_STORAGE_KEY } from "../Utilities/constants.ts";
import PropTypes from "prop-types";

const internalSkuUrl = "api/internalSku";

const isSameInternalSkuOption = (a, b) => {
    if (typeof a !== "object" || typeof b !== "object") return a === b;
    return (
        a?.internalSku === b?.internalSku &&
        a?.sapId === b?.sapId &&
        a?.sku === b?.sku
    );
};

export const InternalSkuContext = React.createContext(null);

export function InternalSkuContextProvider(props) {
    const { selectedDistributors } = useDistributorsContext();
    const [internalSkus, setInternalSkus] = useState([]);
    const [selectedInternalSkus, setSelectedInternalSkus] = useState([]);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (!selectedDistributors || selectedDistributors.length === 0) {
            setInternalSkus([]);
            setSelectedInternalSkus([]);
            setIsReady(true);
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        const query = selectedDistributors
            .map((d) => `distributorIds=${d.distributorId}`)
            .join("&");

        fetch(`${internalSkuUrl}?${query}`, { signal })
            .then((res) => res.json())
            .then((data) => {
                const skuList = Array.isArray(data) ? data : [];
                setInternalSkus(skuList);

                const stored = localStorage.getItem(INTERNAL_SKU_LOCAL_STORAGE_KEY);
                if (stored) {
                    try {
                        const parsed = JSON.parse(stored);
                        const restored = Array.isArray(parsed)
                            ? parsed.filter((s) =>
                                skuList.some((opt) => isSameInternalSkuOption(opt, s))
                            )
                            : [];
                        setSelectedInternalSkus(restored);
                    } catch {
                        setSelectedInternalSkus([]);
                    }
                } else {
                    setSelectedInternalSkus([]);
                }
                setIsReady(true);
            })
            .catch((err) => {
                if (err.name !== "AbortError") {
                    console.error("Error fetching internal SKUs:", err);
                }
                setInternalSkus([]);
                setSelectedInternalSkus([]);
                setIsReady(true);
            });

        return () => controller.abort();
    }, [selectedDistributors]);

    useEffect(() => {
        if (selectedInternalSkus?.length > 0) {
            localStorage.setItem(
                INTERNAL_SKU_LOCAL_STORAGE_KEY,
                JSON.stringify(selectedInternalSkus)
            );
        } else if (selectedDistributors?.length > 0) {
            localStorage.removeItem(INTERNAL_SKU_LOCAL_STORAGE_KEY);
        }
    }, [selectedInternalSkus, selectedDistributors]);

    return (
        <InternalSkuContext.Provider
            value={{
                internalSkus,
                selectedInternalSkus,
                setSelectedInternalSkus,
                isReady,
            }}
        >
            {props.children}
        </InternalSkuContext.Provider>
    );
}

InternalSkuContextProvider.propTypes = {
    children: PropTypes.node,
};

export function useInternalSkuContext() {
    const context = useContext(InternalSkuContext);

    if (!context) {
        throw new Error(
            "useInternalSkuContext must be used within an InternalSkuContextProvider."
        );
    }
    return context;
}
